// ** React Imports
import { Suspense, lazy } from 'react'
import { createRoot } from 'react-dom/client'

// ** Redux Imports
import { Provider } from 'react-redux'
import { store } from './redux/storeConfig/store'

// ** Sentry Imports
import * as Sentry from "@sentry/react";
import { createBrowserHistory } from 'history';

// ** Intl, CASL & ThemeColors Context
import ability from './configs/acl/ability'
import { ToastContainer } from 'react-toastify'
import { AbilityContext } from './utility/context/Can'
import { ThemeContext } from './utility/context/ThemeColors'
import { IntlProviderWrapper } from './utility/context/Internationalization'

// ** Spinner (Splash Screen)
import Spinner from './@core/components/spinner/Fallback-spinner'

// ** PrismJS
import 'prismjs'
import 'prismjs/themes/prism-tomorrow.css'
import 'prismjs/components/prism-jsx.min'

// ** React Perfect Scrollbar
import 'react-perfect-scrollbar/dist/css/styles.css'

// ** React Toastify
import '@styles/react/libs/toastify/toastify.scss'

// ** Core styles
import './@core/assets/fonts/feather/iconfont.css'
import './@core/assets/fonts/avant-garde-gothic/avant-garde-gothic-font.css'
import './@core/scss/core.scss'
import '@fontsource/spartan'
import '@fontsource/spartan/400.css'
import '@fontsource/spartan/500.css'
import '@fontsource/spartan/600.css'
import '@fontsource/spartan/700.css'
import '@fontsource/spartan/800.css'

// ** Service Worker
import * as serviceWorker from './serviceWorker'
import App from './App';

// ** Initialize Sentry
const history = createBrowserHistory();
Sentry.init({
  dsn: "https://a44ff4b70ffa151130ae4ada1f7bdf44@o970154.ingest.sentry.io/4506383982592000",

  sampleRate: 1.0,

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 1.0,

  // This sets the sample rate to be 25%. You may want this to be 100% while
  // in development and sample at a lower rate in production
  replaysSessionSampleRate: 1.0,
  // If the entire session is not sampled, use the below sample rate to sample
  // sessions when an error occurs.
  replaysOnErrorSampleRate: 1.0,
  environment: process.env.REACT_APP_AMPLIFY_ENV || 'development',
  integrations: [
    new Sentry.Replay({
      networkDetailAllowUrls: ['api.getoliver.com', 'core.getoliver.com'],
      maskAllText: false,
      maskAllInputs: false,
    }),
    new Sentry.BrowserTracing({
      routingInstrumentation: Sentry.reactRouterV5Instrumentation(history),
    }),
  ],
});

// ** Lazy load app
const LazyApp = lazy(() => import('./App'));

const container = document.getElementById('root');
const root = createRoot(container);
root.render(
  <Provider store={store}>
    <Suspense fallback={<Spinner />}>
      <AbilityContext.Provider value={ability}>
        <ThemeContext>
          <IntlProviderWrapper>
            <Sentry.ErrorBoundary>
              <App />
            </Sentry.ErrorBoundary>
            <ToastContainer newestOnTop />
          </IntlProviderWrapper>
        </ThemeContext>
      </AbilityContext.Provider>
    </Suspense>
  </Provider>
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister()
